import { useCallback, useEffect, useEffectEvent, useMemo, useState, type ReactNode } from "react";

import { createAcceptanceModalDialogProps, toast, useModalDialog } from "@skilitics/designer-ui-components";
import type { FormatMessages } from "@skilitics/localization";
import { useFormatMessage } from "@skilitics/localization";

import type { CanImportResult } from "../state";
import { NodeLinkagesWarning } from "./NodeLinkagesWarning";
import { useSlotPasteClipboard } from "./useSlotPasteClipboard";

const modalDialogId = "slotPasteDialogId";

const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
const isSafari = navigator.userAgent.toLowerCase().includes("safari");
const browserRequiresUserActivatedClipboardAccess = isFirefox || isSafari;

export const useManagedSlotPasteClipboard = (...args: Parameters<typeof useSlotPasteClipboard>) => {
  const { paste, canPaste } = useSlotPasteClipboard(...args);
  const { openModalDialog, closeModalDialog } = useModalDialog();
  const formatMessage = useFormatMessage(formatStrings);

  const checkClipboard = useEffectEvent(() => {
    (async () => {
      setCanPasteHere(await canPaste());
    })().catch((err) => console.info(err));
  });

  const [canPasteHere, setCanPasteHere] = useState<CanImportResult>(
    // Firefox/Safari: assume true, validate on click
    browserRequiresUserActivatedClipboardAccess || {
      value: false,
      message: "",
    },
  );

  const openDialog = useCallback(
    (message: ReactNode) => {
      const acceptanceDialogProps = createAcceptanceModalDialogProps({
        header: formatMessage("cannotPasteHere"),
        message,
        confirmLabel: formatMessage("close"),
        onConfirm: () => closeModalDialog(modalDialogId),
      });

      openModalDialog(acceptanceDialogProps, modalDialogId);
    },
    [closeModalDialog, formatMessage, openModalDialog],
  );

  const managedPaste = useCallback(() => {
    paste((blocked) => {
      if ("message" in blocked) {
        toast.error(blocked.message);
      } else {
        // Linkage conflicts list affected references, so they stay in a dialog rather than a toast.
        const { associations, configReferences } = blocked;
        openDialog(<NodeLinkagesWarning associations={associations} configReferences={configReferences} />);
      }
    });
  }, [openDialog, paste]);

  useEffect(() => {
    // Firefox/Safari doesn't support this clipboard API.
    if (browserRequiresUserActivatedClipboardAccess) return;

    // Check immediately when the component mounts
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-initialize-state
    checkClipboard();

    // Check if paste is available when:
    // ** the component first mounts (opening a scene/navgroup) or
    // ** the cut event is triggered or
    // ** when the window loses/regains focus (This allows users to copy nodes between windows).
    const events = ["copy", "cut", "focus", "paste"];
    for (const event of events) {
      window.addEventListener(event, checkClipboard);
    }

    return () => {
      for (const event of events) {
        window.removeEventListener(event, checkClipboard);
      }
    };
  }, []);

  return useMemo(
    () => ({
      canPasteHere:
        canPasteHere === true
          ? { value: true, message: "" }
          : "message" in canPasteHere
            ? { value: false, message: canPasteHere.message }
            : {
                value: false,
                message: (
                  <NodeLinkagesWarning
                    associations={canPasteHere.associations}
                    configReferences={canPasteHere.configReferences}
                  />
                ),
              },
      paste: managedPaste,
    }),
    [canPasteHere, managedPaste],
  );
};

const formatStrings = {
  close: {
    en: "Close",
  },
  cannotPasteHere: {
    en: "Cannot paste here",
  },
} satisfies FormatMessages;
